import { effect, Signal, signal } from "@angular/core";
import { ListDataEnhanceRowsContext, ListDataEnhancer } from "../../../modules/s25-table-list/list-data-enhancer";
import { TableEditableDateComponent } from "../../../modules/s25-table/generics/table-editable-date.component";
import { Table } from "../../../modules/s25-table/Table";
import { ListData } from "../../../pojo/ListData";
import { S25Datefilter } from "../../../modules/s25-dateformat/s25.datefilter.service";
import { S25Util } from "../../../util/s25-util";

export type CopyRelatedListEnhancerOptions = {
    showMappingStartDate: Signal<boolean>;
    dayOffset: Signal<number>;
    readOnlyEventId: Signal<number | undefined>;
    pinFirstItemId: Signal<number | undefined>;
};

/**
 * List data enhancer for Copy Related Events: Mapping Start Date column,
 * per-event day offsets, and pinning the current event to the top.
 */
export class CopyRelatedListEnhancer implements ListDataEnhancer {
    static readonly MappingStartDateCol = "mapping_start_date";

    private lastAppliedMappingDayOffset: number | undefined;
    /** Original start date per event id (from list Start Date column). */
    private readonly originalStartByEventId = new Map<number, Date>();
    /** User-edited mapping start dates; cleared when the global dayOffset changes. */
    private readonly mappingStartByEventId = signal(new Map<number, Date>());
    /** Bumped only after a day-offset change that requires a table refresh; starts undefined to skip mount. */
    readonly refreshToken = signal<number | undefined>(undefined);

    constructor(private readonly options: CopyRelatedListEnhancerOptions) {
        effect(() => {
            const showMapping = this.options.showMappingStartDate();
            const dayOffset = this.options.dayOffset();
            if (!showMapping) {
                this.lastAppliedMappingDayOffset = undefined;
                return;
            }
            if (this.lastAppliedMappingDayOffset === undefined) {
                this.lastAppliedMappingDayOffset = dayOffset;
                return;
            }
            if (this.lastAppliedMappingDayOffset === dayOffset) {
                return;
            }
            this.lastAppliedMappingDayOffset = dayOffset;
            // Global offset changed — drop per-row edits and reseed from start + offset
            this.mappingStartByEventId.set(new Map());
            this.refreshToken.update((token) => (token ?? 0) + 1);
        });
    }

    enhanceColumns(columns: Table.Column[]): Table.Column[] {
        if (!this.options.showMappingStartDate()) {
            return columns;
        }

        const startDateIndex = columns.findIndex((column) => column.id === "start_date");
        const mappingColumn: Table.Column = {
            id: CopyRelatedListEnhancer.MappingStartDateCol,
            header: "Mapping Start Date",
            sortable: false,
            type: "date",
        };
        if (startDateIndex >= 0) {
            columns.splice(startDateIndex + 1, 0, mappingColumn);
        } else {
            columns.push(mappingColumn);
        }
        return columns;
    }

    enhanceRows(rows: Table.Row[], listResp: ListData.Wrapper, context: ListDataEnhanceRowsContext): Table.Row[] {
        if (this.options.showMappingStartDate()) {
            this.applyMappingStartDates(rows, listResp, context.dateFormat);
        }
        this.pinFirstItem(rows);
        return rows;
    }

    /** Day offsets implied by Mapping Start Date edits (event id → days from original start). */
    getEventDayOffsets(): Record<number, number> {
        const dayOffset = this.options.dayOffset();
        const overrides = this.mappingStartByEventId();
        const result: Record<number, number> = {};
        for (const [eventId, originalStart] of this.originalStartByEventId) {
            const mappedStart = overrides.get(eventId) ?? S25Util.date.addDays(originalStart, dayOffset);
            result[eventId] = S25Util.date.diffDays(originalStart, mappedStart);
        }
        return result;
    }

    private applyMappingStartDates(
        rows: Table.Row[],
        listResp: ListData.Wrapper,
        dateFormat: string | undefined,
    ): void {
        const startDateColIndex = listResp.cols.findIndex((column) => column.prefname === "start_date");
        const dayOffset = this.options.dayOffset();
        const overrides = this.mappingStartByEventId();

        this.originalStartByEventId.clear();

        for (const [rowIndex, row] of rows.entries()) {
            const eventId = S25Util.toInt(row.id);
            const rawStart = startDateColIndex >= 0 ? listResp.rows[rowIndex]?.row[startDateColIndex] : undefined;
            if (typeof rawStart !== "string" || !dateFormat || !Number.isFinite(eventId)) {
                row.cells[CopyRelatedListEnhancer.MappingStartDateCol] = { text: "—" };
                continue;
            }

            const originalStart = S25Util.date.parse(rawStart);
            this.originalStartByEventId.set(eventId, originalStart);
            const mappedStart = overrides.get(eventId) ?? S25Util.date.addDays(originalStart, dayOffset);
            const isReadOnly = eventId === this.options.readOnlyEventId();

            row.cells[CopyRelatedListEnhancer.MappingStartDateCol] = {
                component: TableEditableDateComponent,
                inputs: {
                    val: mappedStart,
                    dateFormat,
                    readOnly: isReadOnly,
                },
                outputs: isReadOnly
                    ? undefined
                    : {
                          valChange: (date: Date) => this.onMappingStartDateChange(eventId, date),
                      },
                textValue: S25Datefilter.transform(mappedStart, dateFormat) ?? "",
            };
        }
    }

    private pinFirstItem(rows: Table.Row[]): void {
        const pinFirstItemId = this.options.pinFirstItemId();
        if (!Number.isFinite(pinFirstItemId)) {
            return;
        }
        const pinnedIndex = rows.findIndex((row) => Number(row.id) === pinFirstItemId);
        if (pinnedIndex > 0) {
            const [pinnedRow] = rows.splice(pinnedIndex, 1);
            rows.unshift(pinnedRow);
        }
    }

    private onMappingStartDateChange(eventId: number, date: Date): void {
        if (!date || !Number.isFinite(eventId)) {
            return;
        }
        this.mappingStartByEventId.update((current) => {
            const next = new Map(current);
            next.set(eventId, S25Util.date.parseDropTZ(date) ?? date);
            return next;
        });
    }
}
